import { useState } from "react";
import {
  T, Badge, Card, Btn, KV, FSBadge, StBadge,
  fmtCcy, fmtPct, fmtDate, font,
  Inp, Sel, ST
} from '../theme.js';
import { FacilityType, FacilityStatus, RepaymentType, IFRS9Stage } from '../engines/lending-engine.js';


// ============================================================
// PORTFOLIO VIEW
// ============================================================

const Portfolio = ({ lending, onSelect, onNew }) => {
  const facs = lending.getAllFacilities();
  const active = facs.filter(f => f.status === FacilityStatus.ACTIVE);
  const totComm = active.reduce((s, f) => s + f.committedAmount, 0);
  const totDrawn = active.reduce((s, f) => s + f.drawnAmount, 0);
  const totECL = active.reduce((s, f) => s + f.eclProvision, 0);
  const s2plus = active.filter(f => f.ifrs9Stage !== IFRS9Stage.STAGE_1).length;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, color: T.text, margin: 0, fontFamily: font }}>Lending Portfolio</h2>
        <Btn onClick={onNew}>+ New Facility</Btn>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 24 }}>
        <Card style={{ padding: 14, textAlign: 'center' }}><div style={{ fontSize: 11, color: T.textMuted }}>Facilities</div><div style={{ fontSize: 22, fontWeight: 700, color: T.blue, marginTop: 4 }}>{facs.length}</div></Card>
        <Card style={{ padding: 14, textAlign: 'center' }}><div style={{ fontSize: 11, color: T.textMuted }}>Committed</div><div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginTop: 4 }}>{fmtCcy(totComm)}</div></Card>
        <Card style={{ padding: 14, textAlign: 'center' }}><div style={{ fontSize: 11, color: T.textMuted }}>Drawn</div><div style={{ fontSize: 15, fontWeight: 700, color: T.purple, marginTop: 4 }}>{fmtCcy(totDrawn)}</div></Card>
        <Card style={{ padding: 14, textAlign: 'center' }}><div style={{ fontSize: 11, color: T.textMuted }}>ECL Provision</div><div style={{ fontSize: 15, fontWeight: 700, color: T.amber, marginTop: 4 }}>{fmtCcy(totECL)}</div></Card>
        <Card style={{ padding: 14, textAlign: 'center' }}><div style={{ fontSize: 11, color: T.textMuted }}>Stage 2/3</div><div style={{ fontSize: 22, fontWeight: 700, color: s2plus > 0 ? T.red : T.green, marginTop: 4 }}>{s2plus}</div></Card>
      </div>

      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: font }}>
          <thead><tr style={{ background: T.surfaceAlt, borderBottom: `1px solid ${T.border}` }}>
            {['Customer', 'Type', 'Committed', 'Drawn', 'Rate', 'Status', 'IFRS 9', 'ECL'].map(h =>
              <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: T.textSecondary, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
            )}
          </tr></thead>
          <tbody>{facs.map(f => (
            <tr key={f.id} onClick={() => onSelect(f.id)}
              style={{ borderBottom: `1px solid ${T.borderLight}`, cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.background = T.surfaceAlt}
              onMouseLeave={e => e.currentTarget.style.background = ''}>
              <td style={{ padding: '12px 14px', fontWeight: 500, color: T.text }}>{f.customerName}</td>
              <td style={{ padding: '12px 14px', color: T.textSecondary }}>{f.type.replace(/_/g, ' ')}</td>
              <td style={{ padding: '12px 14px' }}>{fmtCcy(f.committedAmount)}</td>
              <td style={{ padding: '12px 14px', color: f.drawnAmount > 0 ? T.purple : T.textMuted }}>{fmtCcy(f.drawnAmount)}</td>
              <td style={{ padding: '12px 14px', color: T.textSecondary }}>{fmtPct(f.effectiveRate)}</td>
              <td style={{ padding: '12px 14px' }}><FSBadge s={f.status} /></td>
              <td style={{ padding: '12px 14px' }}><StBadge s={f.ifrs9Stage} /></td>
              <td style={{ padding: '12px 14px', color: f.eclProvision > 0 ? T.amber : T.textMuted }}>{fmtCcy(f.eclProvision)}</td>
            </tr>
          ))}</tbody>
        </table>
      </Card>
    </div>
  );
};


// ============================================================
// FACILITY DETAIL
// ============================================================

const FacilityDetail = ({ lending, customerAccounts, facilityId, onBack, refresh }) => {
  const [tab, setTab] = useState('overview');
  const f = lending.getFacility(facilityId);
  if (!f) return null;
  const acc = lending.accruals.get(f.id);
  const sched = lending.getSchedule(f.id);
  const custAccountId = customerAccounts[f.customerId];

  const tabs = [
    { k: 'overview', l: 'Overview' },
    { k: 'dd', l: 'Drawdown / Repay' },
    { k: 'interest', l: `Interest (${fmtCcy(acc?.accruedInterest || 0)})` },
    { k: 'ifrs9', l: 'IFRS 9' },
    { k: 'cov', l: `Covenants (${f.covenants.length})` },
    ...(sched.length > 0 ? [{ k: 'sched', l: 'Schedule' }] : []),
    { k: 'tl', l: 'Timeline' },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <Btn variant="ghost" onClick={onBack} small>← Back</Btn>
        <h2 style={{ fontSize: 18, fontWeight: 600, color: T.text, margin: 0, fontFamily: font }}>{f.customerName}</h2>
        <Badge color="purple">{f.type.replace(/_/g, ' ')}</Badge>
        <FSBadge s={f.status} />
        <StBadge s={f.ifrs9Stage} />
      </div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: `1px solid ${T.border}` }}>
        {tabs.map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} style={{
            padding: '8px 14px', fontSize: 13, fontWeight: tab === t.k ? 600 : 400,
            color: tab === t.k ? T.blue : T.textSecondary, background: 'transparent', border: 'none',
            borderBottom: tab === t.k ? `2px solid ${T.blue}` : '2px solid transparent',
            cursor: 'pointer', fontFamily: font, marginBottom: -1,
          }}>{t.l}</button>
        ))}
      </div>

      {tab === 'overview' && <FacilityOverviewTab f={f} acc={acc} lending={lending} refresh={refresh} />}
      {tab === 'dd' && <DDTab f={f} lending={lending} custAccountId={custAccountId} refresh={refresh} />}
      {tab === 'interest' && <IntTab f={f} acc={acc} lending={lending} custAccountId={custAccountId} refresh={refresh} />}
      {tab === 'ifrs9' && <IFRS9Tab f={f} lending={lending} refresh={refresh} />}
      {tab === 'cov' && <CovTab f={f} lending={lending} />}
      {tab === 'sched' && <SchedTab sched={sched} />}
      {tab === 'tl' && <TLTab f={f} />}
    </div>
  );
};


// ============================================================
// TABS
// ============================================================

const FacilityOverviewTab = ({ f, acc, lending, refresh }) => {
  const util = f.committedAmount > 0 ? f.drawnAmount / f.committedAmount * 100 : 0;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      <Card>
        <ST>Facility Terms</ST>
        {[
          ['Type', f.type.replace(/_/g, ' ')],
          ['Committed', fmtCcy(f.committedAmount)],
          ['Currency', f.currency],
          ['Rate', f.interestRate ? `Fixed ${fmtPct(f.interestRate)}` : `${fmtPct(f.marginOverBase || 0)} + ${fmtPct(f.baseRate || 0)} = ${fmtPct(f.effectiveRate)}`],
          ['Tenor', f.tenor || '—'],
          ['Start', fmtDate(f.startDate)],
          ['Maturity', fmtDate(f.maturityDate)],
          ['Repayment', (f.repaymentType || '').replace(/_/g, ' ')],
          ['Frequency', f.repaymentFrequency || '—'],
        ].map(([l, v]) => <KV key={l} label={l} value={v} />)}
      </Card>

      <Card>
        <ST>Utilisation</ST>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: T.textMuted, marginBottom: 6 }}>
          <span>Drawn: {fmtCcy(f.drawnAmount)}</span>
          <span>Available: {fmtCcy(f.committedAmount - f.drawnAmount)}</span>
        </div>
        <div style={{ height: 8, background: T.surfaceAlt, borderRadius: 4, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${util}%`, background: util > 80 ? T.red : util > 50 ? T.amber : T.blue, borderRadius: 4 }} />
        </div>
        <div style={{ textAlign: 'center', fontSize: 14, fontWeight: 700, color: T.text, marginTop: 8 }}>{util.toFixed(1)}%</div>
        <div style={{ borderTop: `1px solid ${T.borderLight}`, paddingTop: 12, marginTop: 12 }}>
          {[['Accrued Int', fmtCcy(acc?.accruedInterest || 0)], ['ECL', fmtCcy(f.eclProvision)], ['IFRS 9', f.ifrs9Stage.replace('_', ' ')]].map(([l, v]) => <KV key={l} label={l} value={v} />)}
        </div>
      </Card>

      <Card style={{ gridColumn: '1 / -1' }}>
        <ST>Actions</ST>
        <div style={{ display: 'flex', gap: 8 }}>
          {f.status === FacilityStatus.DRAFT && <Btn onClick={async () => { await lending.approveFacility(f.id, 'credit_committee'); await refresh(); }}>Approve (Credit Committee)</Btn>}
          {f.status === FacilityStatus.APPROVED && <Btn onClick={async () => { await lending.activateFacility(f.id, 'ops'); await refresh(); }}>Activate Facility</Btn>}
          {f.status === FacilityStatus.ACTIVE && <Btn small variant="secondary" onClick={async () => { await lending.accrueInterest(f.id, new Date()); await refresh(); }}>Run Daily Accrual</Btn>}
        </div>
      </Card>
    </div>
  );
};

const DDTab = ({ f, lending, custAccountId, refresh }) => {
  const [drawAmt, setDrawAmt] = useState('');
  const [repayAmt, setRepayAmt] = useState('');
  const dds = lending.drawdowns.filter(d => d.facilityId === f.id);
  const reps = lending.repayments.filter(r => r.facilityId === f.id);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      <Card>
        <ST>Drawdown</ST>
        <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 12 }}>Available: {fmtCcy(f.committedAmount - f.drawnAmount)}</div>
        <Inp label="Amount (£)" value={drawAmt} onChange={setDrawAmt} placeholder="100000" type="number" disabled={f.status !== FacilityStatus.ACTIVE} />
        <Btn onClick={async () => {
          const amt = parseFloat(drawAmt);
          if (isNaN(amt) || amt <= 0) { alert('Enter a valid positive amount'); return; }
          try { await lending.drawdown(f.id, amt, custAccountId); setDrawAmt(''); await refresh(); }
          catch (e) { alert(e.message); }
        }} disabled={!drawAmt || f.status !== FacilityStatus.ACTIVE}>Draw Down</Btn>
        {dds.length > 0 && (
          <div style={{ marginTop: 16, borderTop: `1px solid ${T.borderLight}`, paddingTop: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, marginBottom: 8 }}>HISTORY</div>
            {dds.map(d => (
              <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '4px 0' }}>
                <span style={{ color: T.textSecondary }}>{fmtDate(d.date)}</span>
                <span style={{ color: T.green, fontWeight: 500 }}>+{fmtCcy(d.amount)}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card>
        <ST>Repayment</ST>
        <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 12 }}>Outstanding: {fmtCcy(f.drawnAmount)}</div>
        <Inp label="Amount (£)" value={repayAmt} onChange={setRepayAmt} placeholder="50000" type="number" disabled={f.status !== FacilityStatus.ACTIVE || f.drawnAmount <= 0} />
        <Btn variant="secondary" onClick={async () => {
          const amt = parseFloat(repayAmt);
          if (isNaN(amt) || amt <= 0) { alert('Enter a valid positive amount'); return; }
          try { await lending.repay(f.id, amt, custAccountId); setRepayAmt(''); await refresh(); }
          catch (e) { alert(e.message); }
        }} disabled={!repayAmt || f.drawnAmount <= 0}>Repay</Btn>
        {reps.length > 0 && (
          <div style={{ marginTop: 16, borderTop: `1px solid ${T.borderLight}`, paddingTop: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, marginBottom: 8 }}>HISTORY</div>
            {reps.map(r => (
              <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '4px 0' }}>
                <span style={{ color: T.textSecondary }}>{fmtDate(r.date)}</span>
                <span style={{ color: T.red, fontWeight: 500 }}>-{fmtCcy(r.amount)}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

const IntTab = ({ f, acc, lending, custAccountId, refresh }) => {
  const entries = acc?.entries || [];
  const last10 = entries.slice(-10);

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        <Card style={{ padding: 14, textAlign: 'center' }}><div style={{ fontSize: 11, color: T.textMuted }}>Rate</div><div style={{ fontSize: 18, fontWeight: 700, color: T.text, marginTop: 4 }}>{fmtPct(f.effectiveRate)}</div></Card>
        <Card style={{ padding: 14, textAlign: 'center' }}><div style={{ fontSize: 11, color: T.textMuted }}>Outstanding</div><div style={{ fontSize: 18, fontWeight: 700, color: T.purple, marginTop: 4 }}>{fmtCcy(f.drawnAmount)}</div></Card>
        <Card style={{ padding: 14, textAlign: 'center' }}><div style={{ fontSize: 11, color: T.textMuted }}>Daily Accrual</div><div style={{ fontSize: 18, fontWeight: 700, marginTop: 4 }}>{fmtCcy(f.drawnAmount * f.effectiveRate / 365)}</div></Card>
        <Card style={{ padding: 14, textAlign: 'center' }}><div style={{ fontSize: 11, color: T.textMuted }}>Uncharged</div><div style={{ fontSize: 18, fontWeight: 700, color: T.amber, marginTop: 4 }}>{fmtCcy(acc?.accruedInterest || 0)}</div></Card>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <Btn small variant="secondary" onClick={async () => { await lending.accrueInterest(f.id, new Date()); await refresh(); }} disabled={f.drawnAmount <= 0}>Accrue Today</Btn>
        <Btn small onClick={async () => {
          if (acc?.accruedInterest > 0) { await lending.chargeAccruedInterest(f.id, custAccountId); await refresh(); }
        }} disabled={!acc || acc.accruedInterest <= 0}>Charge to Customer ({fmtCcy(acc?.accruedInterest || 0)})</Btn>
      </div>

      {last10.length > 0 && (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: font }}>
            <thead><tr style={{ background: T.surfaceAlt }}>
              {['Date', 'Outstanding', 'Daily Interest'].map(h =>
                <th key={h} style={{ padding: '8px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: T.textSecondary, textTransform: 'uppercase' }}>{h}</th>
              )}
            </tr></thead>
            <tbody>{last10.map((e, i) => (
              <tr key={i} style={{ borderBottom: `1px solid ${T.borderLight}` }}>
                <td style={{ padding: '8px 14px', color: T.textSecondary }}>{e.date}</td>
                <td style={{ padding: '8px 14px' }}>{fmtCcy(e.outstanding)}</td>
                <td style={{ padding: '8px 14px', color: T.amber, fontWeight: 500 }}>{fmtCcy(e.amount)}</td>
              </tr>
            ))}</tbody>
          </table>
        </Card>
      )}
    </div>
  );
};

const IFRS9Tab = ({ f, lending, refresh }) => {
  const [newStage, setNewStage] = useState(f.ifrs9Stage);
  const [reason, setReason] = useState('');
  const [pd12, setPd12] = useState('0.02');
  const [pdL, setPdL] = useState('0.10');
  const [lgd, setLgd] = useState('0.40');

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      <Card>
        <ST>Stage Migration</ST>
        <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 12 }}>Current: <StBadge s={f.ifrs9Stage} /></div>
        <Sel label="New Stage" value={newStage} onChange={setNewStage} options={[
          { value: IFRS9Stage.STAGE_1, label: 'Stage 1 — Performing' },
          { value: IFRS9Stage.STAGE_2, label: 'Stage 2 — SICR' },
          { value: IFRS9Stage.STAGE_3, label: 'Stage 3 — Credit Impaired' },
        ]} />
        <Inp label="Reason" value={reason} onChange={setReason} placeholder="e.g. 30+ DPD, covenant breach..." />
        <Btn small onClick={async () => { await lending.migrateStage(f.id, newStage, reason || 'Manual'); setReason(''); await refresh(); }} disabled={newStage === f.ifrs9Stage}>Migrate Stage</Btn>
        <div style={{ marginTop: 16, borderTop: `1px solid ${T.borderLight}`, paddingTop: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: T.textMuted, marginBottom: 8 }}>STAGE HISTORY</div>
          {f.ifrs9History.map((h, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '4px 0', borderBottom: `1px solid ${T.borderLight}` }}>
              <div><StBadge s={h.stage} /> <span style={{ color: T.textMuted, marginLeft: 6 }}>{h.reason}</span></div>
              <span style={{ color: T.textMuted }}>{fmtDate(h.at)}</span>
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <ST>ECL Calculation</ST>
        <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 12 }}>EAD: {fmtCcy(f.drawnAmount)} · Current ECL: {fmtCcy(f.eclProvision)}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
          <Inp label="PD 12m" value={pd12} onChange={setPd12} type="number" />
          <Inp label="PD Lifetime" value={pdL} onChange={setPdL} type="number" />
          <Inp label="LGD" value={lgd} onChange={setLgd} type="number" />
        </div>
        <Btn small onClick={async () => {
          const r = await lending.calculateECL(f.id, { pd12m: parseFloat(pd12), pdLifetime: parseFloat(pdL), lgd: parseFloat(lgd) });
          await refresh();
          alert(`ECL: ${fmtCcy(r.ecl)} (${r.stage === IFRS9Stage.STAGE_1 ? '12m PD' : 'Lifetime PD'})\nChange: ${fmtCcy(r.change)}`);
        }}>Calculate ECL</Btn>
        <div style={{ marginTop: 16, padding: 12, background: f.ifrs9Stage === IFRS9Stage.STAGE_1 ? T.greenBg : f.ifrs9Stage === IFRS9Stage.STAGE_2 ? T.amberBg : T.redBg, borderRadius: 6, fontSize: 12 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            {f.ifrs9Stage === IFRS9Stage.STAGE_1 ? 'Stage 1: 12-month ECL' : f.ifrs9Stage === IFRS9Stage.STAGE_2 ? 'Stage 2: Lifetime ECL (SICR)' : 'Stage 3: Lifetime ECL (Credit Impaired)'}
          </div>
          <div style={{ color: T.textSecondary }}>
            {f.ifrs9Stage === IFRS9Stage.STAGE_1 ? 'Using 12-month PD × LGD × EAD' : 'Using Lifetime PD × LGD × EAD'}
          </div>
        </div>
      </Card>
    </div>
  );
};

const CovTab = ({ f, lending }) => {
  const [data, setData] = useState(() => { const d = {}; f.covenants.forEach(c => d[c.metric] = ''); return d; });
  const [result, setResult] = useState(null);
  const run = async () => {
    const numData = {};
    Object.entries(data).forEach(([k, v]) => numData[k] = parseFloat(v) || 0);
    setResult(await lending.checkCovenants(f.id, numData));
  };

  return (
    <div>
      {f.covenants.length === 0
        ? <Card style={{ textAlign: 'center', padding: 40, color: T.textMuted }}>No covenants on this facility.</Card>
        : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Card>
              <ST>Financial Data Input</ST>
              {f.covenants.map(c => (
                <Inp key={c.metric} label={`${c.description || c.metric} (${c.type} ${c.threshold})`}
                  value={data[c.metric]} onChange={v => setData(d => ({ ...d, [c.metric]: v }))}
                  type="number" placeholder={`Threshold: ${c.threshold}`} />
              ))}
              <Btn small onClick={run}>Check Covenants</Btn>
            </Card>
            <Card>
              <ST>Results</ST>
              {!result
                ? <div style={{ color: T.textMuted, fontSize: 13, padding: 20, textAlign: 'center' }}>Enter financial data and check.</div>
                : (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                      <div style={{ width: 10, height: 10, borderRadius: '50%', background: result.anyBreach ? T.red : T.green }} />
                      <span style={{ fontSize: 14, fontWeight: 600, color: result.anyBreach ? T.red : T.green }}>
                        {result.anyBreach ? 'BREACH DETECTED' : 'All Covenants Met'}
                      </span>
                    </div>
                    {result.results.map((r, i) => (
                      <div key={i} style={{
                        padding: '10px 12px', marginBottom: 6, borderRadius: 6,
                        background: r.breached ? T.redBg : T.greenBg,
                        border: `1px solid ${r.breached ? T.redBorder : T.greenBorder}`,
                      }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: r.breached ? T.red : T.green }}>{r.description || r.metric}</div>
                        <div style={{ fontSize: 11, color: T.textSecondary, marginTop: 2 }}>
                          Required: {r.type} {r.threshold} · Actual: {r.actual?.toFixed(2) || '—'} {r.breached ? '⚠ BREACH' : '✓'}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
            </Card>
          </div>
        )}
    </div>
  );
};

const SchedTab = ({ sched }) => (
  <Card style={{ padding: 0, overflow: 'hidden' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: font }}>
      <thead><tr style={{ background: T.surfaceAlt }}>
        {['#', 'Date', 'Principal Due', 'Status'].map(h =>
          <th key={h} style={{ padding: '8px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: T.textSecondary, textTransform: 'uppercase' }}>{h}</th>
        )}
      </tr></thead>
      <tbody>{sched.slice(0, 24).map((s, i) => (
        <tr key={i} style={{ borderBottom: `1px solid ${T.borderLight}` }}>
          <td style={{ padding: '8px 14px', color: T.textMuted }}>{s.i}</td>
          <td style={{ padding: '8px 14px' }}>{s.date}</td>
          <td style={{ padding: '8px 14px', fontWeight: 500 }}>{fmtCcy(s.principal)}</td>
          <td style={{ padding: '8px 14px' }}><Badge color={s.status === 'PENDING' ? 'gray' : 'green'}>{s.status}</Badge></td>
        </tr>
      ))}</tbody>
    </table>
    {sched.length > 24 && <div style={{ padding: 12, textAlign: 'center', fontSize: 12, color: T.textMuted }}>Showing first 24 of {sched.length} installments</div>}
  </Card>
);

const TLTab = ({ f }) => (
  <Card style={{ padding: 0 }}>
    {f.statusHistory.map((h, i) => (
      <div key={i} style={{ display: 'flex', gap: 16, padding: '12px 16px', borderBottom: i < f.statusHistory.length - 1 ? `1px solid ${T.borderLight}` : 'none' }}>
        <div style={{ width: 80, fontSize: 11, color: T.textMuted }}>{fmtDate(h.at)}</div>
        <div style={{ width: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: i === f.statusHistory.length - 1 ? T.blue : T.border, marginTop: 4 }} />
          {i < f.statusHistory.length - 1 && <div style={{ width: 1, flex: 1, background: T.border, marginTop: 4 }} />}
        </div>
        <div>
          <FSBadge s={h.status} />
          <div style={{ fontSize: 12, color: T.textSecondary, marginTop: 4 }}>{h.by}</div>
        </div>
      </div>
    ))}
  </Card>
);


// ============================================================
// NEW FACILITY FORM
// ============================================================

const NewFacility = ({ lending, onDone, onCancel }) => {
  const [f, setF] = useState({
    customerName: '', type: FacilityType.REVOLVING_CREDIT,
    committedAmount: '', interestRate: '', marginOverBase: '', baseRate: '',
    tenor: '', maturityDate: '', repaymentType: RepaymentType.INTEREST_ONLY,
  });

  const create = async () => {
    try {
      const amt = parseFloat(f.committedAmount);
      if (isNaN(amt) || amt <= 0) { alert('Committed amount must be a positive number'); return; }
      const fac = await lending.createFacility({
        customerId: `cust_new_${Date.now()}`, // Shell should provide real customerId
        customerName: f.customerName,
        type: f.type,
        committedAmount: amt,
        interestRate: f.interestRate ? parseFloat(f.interestRate) : null,
        marginOverBase: f.marginOverBase ? parseFloat(f.marginOverBase) : null,
        baseRate: f.baseRate ? parseFloat(f.baseRate) : null,
        tenor: f.tenor,
        maturityDate: f.maturityDate || undefined,
        repaymentType: f.repaymentType,
      });
      onDone(fac.id);
    } catch (e) { alert(e.message); }
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <Btn variant="ghost" onClick={onCancel} small>← Back</Btn>
        <h2 style={{ fontSize: 18, fontWeight: 600, color: T.text, margin: 0, fontFamily: font }}>New Lending Facility</h2>
      </div>
      <Card style={{ maxWidth: 600 }}>
        <Inp label="Customer Name *" value={f.customerName} onChange={v => setF(x => ({ ...x, customerName: v }))} placeholder="Acme Corp Ltd" />
        <Sel label="Facility Type" value={f.type} onChange={v => setF(x => ({ ...x, type: v }))} options={[
          { value: FacilityType.TERM_LOAN, label: 'Term Loan' },
          { value: FacilityType.REVOLVING_CREDIT, label: 'Revolving Credit' },
          { value: FacilityType.OVERDRAFT, label: 'Overdraft' },
        ]} />
        <Inp label="Committed Amount (£) *" value={f.committedAmount} onChange={v => setF(x => ({ ...x, committedAmount: v }))} placeholder="500000" type="number" />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
          <Inp label="Fixed Rate (decimal)" value={f.interestRate} onChange={v => setF(x => ({ ...x, interestRate: v }))} placeholder="0.065" type="number" />
          <Inp label="Margin over Base" value={f.marginOverBase} onChange={v => setF(x => ({ ...x, marginOverBase: v }))} placeholder="0.025" type="number" />
          <Inp label="Base Rate" value={f.baseRate} onChange={v => setF(x => ({ ...x, baseRate: v }))} placeholder="0.0475" type="number" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <Inp label="Tenor" value={f.tenor} onChange={v => setF(x => ({ ...x, tenor: v }))} placeholder="3Y" />
          <Inp label="Maturity Date" value={f.maturityDate} onChange={v => setF(x => ({ ...x, maturityDate: v }))} placeholder="2028-06-30" />
        </div>
        <Sel label="Repayment Type" value={f.repaymentType} onChange={v => setF(x => ({ ...x, repaymentType: v }))} options={[
          { value: RepaymentType.INTEREST_ONLY, label: 'Interest Only' },
          { value: RepaymentType.AMORTISING, label: 'Amortising' },
          { value: RepaymentType.BULLET, label: 'Bullet' },
        ]} />
        <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
          <Btn onClick={create}>Create Facility</Btn>
          <Btn variant="secondary" onClick={onCancel}>Cancel</Btn>
        </div>
      </Card>
    </div>
  );
};


// LENDING MODULE (top-level entry point)
// ============================================================

export default function LendingModule({ lending, customerAccounts, refresh }) {
  const [lendingView, setLendingView] = useState('portfolio');
  const [selectedFacility, setSelectedFacility] = useState(null);

  return (
    <div>
      {lendingView === 'portfolio' && <Portfolio lending={lending} onSelect={id => { setSelectedFacility(id); setLendingView('detail'); }} onNew={() => setLendingView('new')} />}
      {lendingView === 'detail' && selectedFacility && <FacilityDetail lending={lending} customerAccounts={customerAccounts} facilityId={selectedFacility} onBack={() => { setLendingView('portfolio'); setSelectedFacility(null); }} refresh={refresh} />}
      {lendingView === 'new' && <NewFacility lending={lending} onDone={id => { setSelectedFacility(id); setLendingView('detail'); refresh(); }} onCancel={() => setLendingView('portfolio')} />}
    </div>
  );
}
